import React from "react";
import {
  ArrowUpRight,
  CheckCircle2,
  Zap,
  Wallet,
  FileText,
  UserCog,
  AlertTriangle,
  BarChart3,
  ListChecks,
  UploadCloud,
  Gauge,
  Tags,
  Code2,
  Award,
} from "lucide-react";
import { tools, trustStats } from "../data/mock";
import { Reveal } from "./ui/Reveal";

// Icon per USP, matched by position within each tool's `usps` array in mock.js.
const uspIcons = [
  [Zap, Wallet, FileText, UserCog],
  [AlertTriangle, BarChart3, ListChecks, UploadCloud],
  [Gauge, Tags, Code2, Award],
];

const ToolFeature = ({ tool, toolIndex, reverse }) => {
  const icons = uspIcons[toolIndex] || [];

  return (
    <div className="max-w-[1280px] mx-auto px-6 py-16">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-14 items-center">
        {/* Image, with a floating trust-badge card overlapping the corner */}
        <Reveal className={reverse ? "lg:order-2" : ""}>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden group">
              <img
                src={tool.image.src}
                alt={tool.image.alt}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              />
            </div>
            <div className="absolute top-5 right-5 bg-white rounded-xl shadow-lg border border-black/5 px-4 py-3 flex items-center gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <div>
                <div className="text-xs font-semibold text-neutral-950 leading-none">Free to try</div>
                <div className="text-[11px] text-neutral-500 mt-1 leading-none">No signup required</div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Content */}
        <Reveal delay={0.15} className={reverse ? "lg:order-1" : ""}>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-mono tracking-[0.14em] uppercase text-neutral-500 border border-black/10 rounded-full px-3 py-1.5 bg-neutral-50">
            Tool
          </span>
          <h2 className="mt-5 text-[26px] sm:text-[36px] leading-[1.1] tracking-[-0.02em] font-semibold text-neutral-950">
            {tool.headline}
          </h2>
          <p className="mt-4 text-neutral-600 leading-relaxed">{tool.desc}</p>

          {/* Single primary CTA with the blurred gradient glow */}
          <div className="mt-7 relative inline-flex group">
            <div className="absolute -inset-1 rounded-xl opacity-60 blur-lg bg-gradient-to-r from-cyan-400 via-blue-500 to-fuchsia-500 transition-opacity duration-300 group-hover:opacity-90" />
            <a
              href={tool.href}
              target="_blank"
              rel="noopener noreferrer"
              className="relative z-10 inline-flex items-center gap-2 bg-black text-white pl-5 pr-4 py-3 rounded-xl text-[15px] font-medium transition-transform duration-200 group-hover:-translate-y-0.5"
            >
              {tool.cta}
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          {/* USPs — compact single-column stacked pills */}
          <div className="mt-8 flex flex-col gap-2.5">
            {tool.usps.map((usp, i) => {
              const Icon = icons[i] || CheckCircle2;
              return (
                <div
                  key={usp}
                  className="flex items-center gap-3 border border-black/10 rounded-full px-4 py-2 w-fit bg-white"
                >
                  <span className="w-6 h-6 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-3.5 h-3.5 text-neutral-700" />
                  </span>
                  <span className="text-sm text-neutral-700">{usp}</span>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </div>
  );
};

const Solution = () => {
  return (
    <section className="bg-white">
      <div className="pt-24 pb-4">
        <Reveal>
          <div className="max-w-2xl mx-auto px-6 text-center">
            <span className="inline-block text-[11px] tracking-[0.18em] uppercase text-neutral-500 border border-black/10 rounded-full px-3 py-1.5 bg-white">Tools</span>
            <h2 className="mt-5 text-[32px] sm:text-[44px] leading-[1.05] tracking-[-0.02em] font-semibold text-neutral-950">Try the same tools I use to run your growth</h2>
            <p className="mt-4 text-neutral-600">These aren't marketing gimmicks. They're the actual systems behind every campaign run. Try them free before you talk to me.</p>
          </div>
        </Reveal>
      </div>

      {tools.map((tool, i) => (
        <div key={tool.title} className={i % 2 === 1 ? "bg-neutral-50" : "bg-white"}>
          <ToolFeature tool={tool} toolIndex={i} reverse={i === 1} />
        </div>
      ))}

      <div className="py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
            {trustStats.map((stat, i) => (
              <Reveal key={stat.label} delay={i * 0.08}>
                <div className="text-[44px] sm:text-[56px] font-semibold tracking-tight text-neutral-950 leading-none">{stat.value}</div>
                <div className="text-base text-neutral-500 mt-3">{stat.label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Solution;
